import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Task } from '../../types/jobcard';
import { getTaskImageUrl } from '../../config';

interface TaskImageModalProps {
    isOpen: boolean;
    onClose: () => void;
    tasks: Task[];
    currentIndex: number;
    onNext: () => void;
    onPrev: () => void;
}

export default function TaskImageModal({
    isOpen,
    onClose,
    tasks,
    currentIndex,
    onNext,
    onPrev
}: TaskImageModalProps) {
    if (!isOpen) return null;

    const currentTask = tasks[currentIndex];
    const hasNext = currentIndex < tasks.filter(t => t.pictures && t.pictures.length > 0).length - 1;
    const hasPrev = currentIndex > 0;

    return (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full mx-4 relative p-6">
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
                >
                    <X size={24} />
                </button>

                <div className="mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{currentTask.description}</h3>
                    {currentTask.spare && (
                        <p className="text-gray-600 dark:text-gray-400 mt-2">
                            Spare Part: {currentTask.spare.name} (Qty: {currentTask.spare_quantity})
                        </p>
                    )}
                </div>

                <div className="relative">
                    {currentTask.pictures && currentTask.pictures.length > 0 && (
                        <img
                            src={getTaskImageUrl(currentTask.pictures[0].picture)}
                            alt="Task"
                            className="max-h-[70vh] mx-auto rounded-lg"
                            onError={(e) => {
                                console.error(`Failed to load image: ${currentTask.pictures[0].picture}`, e);
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                // Show a fallback message
                                const fallback = document.createElement('div');
                                fallback.className = 'max-h-[70vh] mx-auto rounded-lg flex items-center justify-center text-gray-500 text-lg bg-gray-100 dark:bg-gray-700 p-8';
                                fallback.textContent = 'Task image not available';
                                target.parentNode?.appendChild(fallback);
                            }}
                        />
                    )}

                    {hasPrev && (
                        <button
                            onClick={onPrev}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                            <ChevronLeft size={24} className="text-gray-900 dark:text-gray-200" />
                        </button>
                    )}

                    {hasNext && (
                        <button
                            onClick={onNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-full p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
                        >
                            <ChevronRight size={24} className="text-gray-900 dark:text-gray-200" />
                        </button>
                    )}
                </div>

                <div className="text-center mt-4 text-gray-500 dark:text-gray-400">
                    Image {currentIndex + 1} of {tasks.filter(t => t.pictures && t.pictures.length > 0).length}
                </div>
            </div>
        </div>
    );
} 